<script setup>
import { computed } from 'vue';

import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Official deposit accounts (bot_config payments.accounts). The outbound guard
// blocks every other account number in a bot reply, so this list is the only
// way to let the agent give one out.
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  // (path) => server validation message, e.g. 'payments.accounts[0].number'.
  errorFor: {
    type: Function,
    default: () => '',
  },
});

const emit = defineEmits(['update:modelValue']);

const rows = computed(() =>
  props.modelValue.map((account, index) => ({
    ...account,
    enabled: account.enabled !== false,
    key: index,
  }))
);

const update = (key, field, value) => {
  const next = props.modelValue.map((account, index) =>
    index === key ? { ...account, [field]: value } : account
  );
  emit('update:modelValue', next);
};

const addRow = () => {
  emit('update:modelValue', [
    ...props.modelValue,
    { provider: '', number: '', holder: '', enabled: true },
  ]);
};

const removeRow = key => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_account, index) => index !== key)
  );
};

const rowError = key =>
  props.errorFor(`payments.accounts[${key}].provider`) ||
  props.errorFor(`payments.accounts[${key}].number`);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-col gap-0.5">
      <span class="text-label text-n-slate-12">
        {{ $t('AI_AGENT.HANDOFF.PAYMENTS.ACCOUNTS') }}
      </span>
      <p class="text-label-small text-n-slate-10">
        {{ $t('AI_AGENT.HANDOFF.PAYMENTS.ACCOUNTS_HELP') }}
      </p>
    </div>
    <p
      v-if="!rows.length"
      class="px-3 py-2.5 rounded-lg text-label-small text-n-slate-11 bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      data-testid="payment-accounts-empty"
    >
      {{ $t('AI_AGENT.HANDOFF.PAYMENTS.EMPTY') }}
    </p>
    <div
      v-for="row in rows"
      :key="row.key"
      class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      :class="{ 'opacity-60': !row.enabled }"
      :data-testid="`payment-account-row-${row.key}`"
    >
      <div
        class="grid grid-cols-2 md:grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)_auto_auto] items-center gap-2"
      >
        <Input
          :model-value="row.provider"
          :placeholder="$t('AI_AGENT.HANDOFF.PAYMENTS.PROVIDER_PLACEHOLDER')"
          :data-testid="`payment-account-provider-${row.key}`"
          @keydown.enter.prevent
          @update:model-value="update(row.key, 'provider', $event)"
        />
        <Input
          :model-value="row.number"
          :placeholder="$t('AI_AGENT.HANDOFF.PAYMENTS.NUMBER_PLACEHOLDER')"
          :data-testid="`payment-account-number-${row.key}`"
          @keydown.enter.prevent
          @update:model-value="update(row.key, 'number', $event)"
        />
        <Input
          :model-value="row.holder"
          :placeholder="$t('AI_AGENT.HANDOFF.PAYMENTS.HOLDER_PLACEHOLDER')"
          class="col-span-2 md:col-span-1"
          :data-testid="`payment-account-holder-${row.key}`"
          @keydown.enter.prevent
          @update:model-value="update(row.key, 'holder', $event)"
        />
        <label class="flex items-center gap-2 text-label-small text-n-slate-11">
          <Switch
            :model-value="row.enabled"
            :data-testid="`payment-account-enabled-${row.key}`"
            @update:model-value="update(row.key, 'enabled', $event)"
          />
          {{ $t('AI_AGENT.HANDOFF.PAYMENTS.ENABLED') }}
        </label>
        <Button
          type="button"
          icon="i-woot-bin"
          slate
          ghost
          sm
          class="justify-self-end hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          :aria-label="$t('AI_AGENT.HANDOFF.PAYMENTS.REMOVE')"
          :data-testid="`payment-account-remove-${row.key}`"
          @click="removeRow(row.key)"
        />
      </div>
      <p v-if="rowError(row.key)" class="px-1 text-label-small text-n-ruby-11">
        {{ rowError(row.key) }}
      </p>
    </div>
    <div>
      <Button
        type="button"
        icon="i-lucide-plus"
        faded
        slate
        sm
        :label="$t('AI_AGENT.HANDOFF.PAYMENTS.ADD')"
        data-testid="payment-account-add"
        @click="addRow"
      />
    </div>
  </div>
</template>
